import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Upload, Loader2, Trash2, FileText, Users, BookOpen, DollarSign, CalendarClock, Headphones } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import NarrateControls from "@/components/NarrateControls";
import AdminGifts from "@/components/reader/AdminGifts";
import GiftBannerControl from "@/components/reader/GiftBannerControl";

const cfg = { withCredentials: true };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-white/10 bg-[#121212] p-5">
      <Icon className="w-4 h-4 text-primary-gold" />
      <p className="text-soft font-mono-ui text-[10px] uppercase tracking-[0.2em] mt-3">{label}</p>
      <p className="font-serif-display text-2xl mt-1">{value}</p>
    </div>
  );
}

function BookSlot({ slot, title, book, onChanged }) {
  const [progress, setProgress] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.(pdf|docx)$/i.test(file.name)) return toast.error("Please choose a .pdf or .docx file.");
    const form = new FormData();
    form.append("product_id", slot);
    form.append("file", file);
    setProgress(0);
    try {
      const { data } = await axios.post(`${API}/admin/reader/books`, form, {
        ...cfg,
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      toast.success(`${title} added — ${data.page_count} pages ready.`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setProgress(null);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${title} from the e-reader?`)) return;
    try {
      await axios.delete(`${API}/admin/reader/books/${book.id}`, cfg);
      toast.success(`${title} removed from the e-reader.`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove the book.");
    }
  };

  return (
    <div className="border border-white/10 bg-[#121212] p-6 flex flex-col gap-4" data-testid={`admin-reader-slot-${slot}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-serif-display text-xl leading-tight">{title}</h3>
        {book && (
          <button onClick={remove} className="text-soft hover:text-[#d6453a]" data-testid={`admin-reader-remove-${slot}`} aria-label="Remove">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      {book ? (
        <div className="text-xs text-soft font-mono-ui space-y-1.5">
          <p className="flex items-center gap-2 text-text-primary"><FileText className="w-3.5 h-3.5 text-primary-gold" /> {book.original_filename}</p>
          <p>{book.page_count} pages · uploaded {fmtDate(book.created_at)}</p>
          <p>Narrated: male {book.narrated_pages?.male ?? 0} / female {book.narrated_pages?.female ?? 0} of {book.page_count}</p>
        </div>
      ) : (
        <p className="text-xs text-soft font-mono-ui">Not on the e-reader yet.</p>
      )}
      {book && (
        <Link
          to={`/reader/read/${book.id}`}
          data-testid={`admin-reader-open-${slot}`}
          className="btn-gold flex items-center justify-center gap-2 px-4 py-2.5 font-mono-ui text-xs uppercase tracking-[0.18em]"
        >
          <Headphones className="w-4 h-4" /> Read & listen
        </Link>
      )}
      {book && <NarrateControls book={book} onChanged={onChanged} />}
      <label className="mt-auto btn-ghost cursor-pointer flex items-center justify-center gap-2 px-4 py-2.5 font-mono-ui text-xs uppercase tracking-[0.18em]">
        {progress !== null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {progress !== null ? (progress < 100 ? `Uploading ${progress}%` : "Processing pages…") : book ? "Replace file" : "Upload PDF or Word"}
        <input type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" disabled={progress !== null} onChange={(e) => upload(e.target.files[0])} data-testid={`admin-reader-upload-${slot}`} />
      </label>
    </div>
  );
}

export default function AdminReader() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/admin/reader`, cfg).then((r) => setData(r.data)).catch(() => toast.error("Could not load e-reader data."));
  }, []);
  useEffect(load, [load]);
  const anyRunning = data?.books.some((b) => b.job?.status === "running");
  useEffect(() => {
    if (!anyRunning) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [anyRunning, load]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-primary-gold animate-spin" /></div>;
  const now = new Date();

  return (
    <div data-testid="admin-reader-panel">
      <GiftBannerControl />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat icon={Users} label="Registered readers" value={data.summary.readers} />
        <Stat icon={CalendarClock} label="Active passes" value={data.summary.active_passes} />
        <Stat icon={BookOpen} label="Passes sold" value={`${data.summary.passes_sold}${data.summary.gifts_sold ? ` (${data.summary.gifts_sold} gifts)` : ""}`} />
        <Stat icon={DollarSign} label="Pass revenue" value={`$${data.summary.revenue.toFixed(2)}`} />
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
        <h2 className="font-serif-display text-2xl">E-Reader Books</h2>
        <Link to="/reader" data-testid="admin-open-reader-library" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold">
          <Headphones className="w-4 h-4" /> Open my E-Reader library →
        </Link>
      </div>
      <p className="text-soft text-xs font-mono-ui mb-6">Upload a text-based PDF or Word (.docx) file for each book. Narration is created the first time a page is played and then saved. Use "Narrate whole book" to prepare it in advance so readers never wait.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {Object.entries(data.slots).map(([slot, title]) => (
          <BookSlot key={slot} slot={slot} title={title} book={data.books.find((b) => b.product_id === slot)} onChanged={load} />
        ))}
      </div>

      <h2 className="font-serif-display text-2xl mb-6">Pass Purchases</h2>
      <div className="border border-white/10 overflow-x-auto" data-testid="admin-reader-passes">
        <table className="w-full text-sm">
          <thead className="bg-[#121212] text-soft font-mono-ui text-[10px] uppercase tracking-[0.2em]">
            <tr>{["Reader", "Purchased", "Expires", "Amount", "Status"].map((h) => <th key={h} className="text-left px-5 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.passes.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-soft">No passes sold yet.</td></tr>
            ) : (
              data.passes.map((p) => (
                <tr key={p.id} className="border-t border-white/5">
                  <td className="px-5 py-3">{p.email}</td>
                  <td className="px-5 py-3 text-soft">{fmtDate(p.created_at)}</td>
                  <td className="px-5 py-3 text-soft">{fmtDate(p.expires_at)}</td>
                  <td className="px-5 py-3">{p.source === "gift" ? <span className="text-primary-gold">Gift</span> : `$${p.amount.toFixed(2)}`}</td>
                  <td className="px-5 py-3">{new Date(p.expires_at) > now ? <span className="text-[#4ade80]">Active</span> : <span className="text-soft">Expired</span>}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <AdminGifts gifts={data.gifts || []} />
    </div>
  );
}
